/**
 * Every API path this app calls, in one place.
 *
 * This mirrors the API surface specified in §10.4 and nothing else. A path that
 * is not in the API is a bug in *this file* — not a reason to add a fetch
 * somewhere else. If a screen needs an endpoint that does not exist, it renders
 * against what does and leaves a `TODO(api):` naming the missing route.
 *
 * Paths are relative to `API_BASE` in `./client`, which supplies the `/api/v1`
 * prefix. Every path string in this app appears exactly once, here.
 */

const auth = {
  signup: '/auth/signup',
  login: '/auth/login',
  logout: '/auth/logout',
  /** Confirms the address from the emailed token. There is no resend route. */
  verifyEmail: '/auth/verify-email',
  twoFactorEnroll: '/auth/2fa/enroll',
  /** Confirms an enrolment *and* answers a login challenge. One route, both. */
  twoFactorVerify: '/auth/2fa/verify',
  /** Needs the current password even with a live session; revokes the others. */
  password: '/auth/password',
  sessions: '/auth/sessions',
  session: (sessionId: string) => `/auth/sessions/${sessionId}`,
} as const;

const me = {
  profile: '/me',
  closure: '/me/closure',
  billing: '/me/billing',
  paymentMethod: '/me/billing/payment-method',
  charges: '/me/charges',
  credits: '/me/credits',
  creditPurchase: '/me/credits/purchase',
  notifications: '/me/notifications',
  notificationsRead: '/me/notifications/read',
  notificationPreferences: '/me/notification-preferences',
} as const;

const bots = {
  collection: '/bots',
  one: (botId: string) => `/bots/${botId}`,
  activation: (botId: string) => `/bots/${botId}/activation`,
  transfer: (botId: string) => `/bots/${botId}/transfer`,
  character: (botId: string) => `/bots/${botId}/character`,
  characterVersions: (botId: string) => `/bots/${botId}/character/versions`,
  socialAccounts: (botId: string) => `/bots/${botId}/social-accounts`,
  socialAccount: (botId: string, accountId: string) =>
    `/bots/${botId}/social-accounts/${accountId}`,
  socialAccountReauthorization: (botId: string, accountId: string) =>
    `/bots/${botId}/social-accounts/${accountId}/reauthorization`,
  health: (botId: string) => `/bots/${botId}/health`,
  /** §5.2 runtime transitions. */
  transition: (botId: string, transition: 'run' | 'pause' | 'stop') =>
    `/bots/${botId}/${transition}`,
  schedules: (botId: string) => `/bots/${botId}/schedules`,
  schedule: (botId: string, scheduleId: string) => `/bots/${botId}/schedules/${scheduleId}`,
  schedulePreview: (botId: string) => `/bots/${botId}/schedules/preview`,
  activity: (botId: string) => `/bots/${botId}/activity`,
  spendCap: (botId: string) => `/bots/${botId}/spend-cap`,
  /**
   * DM. Reachable from both nav branches — a renter holding `read_dm` sees the
   * same inbox the owner does, because a rental that cannot answer the persona's
   * enquiries is not the thing being rented.
   */
  dmConversations: (botId: string) => `/bots/${botId}/dm/conversations`,
  dmMessages: (botId: string, conversationId: string) =>
    `/bots/${botId}/dm/conversations/${conversationId}/messages`,
  dmSync: (botId: string) => `/bots/${botId}/dm/sync`,
  listings: (botId: string) => `/bots/${botId}/listings`,
} as const;

const listings = {
  collection: '/listings',
  one: (listingId: string) => `/listings/${listingId}`,
  availability: (listingId: string) => `/listings/${listingId}/availability`,
} as const;

const rentals = {
  collection: '/rentals',
  disclosure: '/rentals/disclosure',
  one: (rentalId: string) => `/rentals/${rentalId}`,
  approval: (rentalId: string) => `/rentals/${rentalId}/approval`,
  decline: (rentalId: string) => `/rentals/${rentalId}/decline`,
  cancellation: (rentalId: string) => `/rentals/${rentalId}/cancellation`,
  consent: (rentalId: string) => `/rentals/${rentalId}/consent`,
  review: (rentalId: string) => `/rentals/${rentalId}/review`,
  /** Reviews are keyed by the rental they came out of, not by the bot. */
  reviews: (rentalId: string) => `/rentals/${rentalId}/reviews`,
} as const;

const content = {
  generationQuote: (botId: string) => `/bots/${botId}/generations/quote`,
  generations: (botId: string) => `/bots/${botId}/generations`,
  generation: (generationId: string) => `/generations/${generationId}`,
  assets: (botId: string) => `/bots/${botId}/assets`,
  asset: (assetId: string) => `/assets/${assetId}`,
  actions: (botId: string) => `/bots/${botId}/actions`,
  action: (actionId: string) => `/actions/${actionId}`,
  actionApproval: (actionId: string) => `/actions/${actionId}/approval`,
  feed: (botId: string) => `/bots/${botId}/feed`,
  search: (botId: string) => `/bots/${botId}/search`,
} as const;

const listening = {
  rules: (botId: string) => `/bots/${botId}/listening-rules`,
  rule: (ruleId: string) => `/listening-rules/${ruleId}`,
  matches: (botId: string) => `/bots/${botId}/listening-matches`,
  matchSend: (matchId: string) => `/listening-matches/${matchId}/send`,
  matchDismiss: (matchId: string) => `/listening-matches/${matchId}/dismiss`,
} as const;

/**
 * One namespace so a screen's own `bots` or `listings` variable cannot shadow
 * the path table by accident.
 */
export const routes = { auth, me, bots, listings, rentals, content, listening } as const;
